import React, { useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIngredientGroupModal from "./EditIngredientGroupModal";
import { igroupToken } from "../../utils/postContent";

function formatAmount(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount ?? "");
  return Number.isInteger(n) ? String(n) : String(n);
}

function parseItems(itemsJson) {
  try {
    const parsed = JSON.parse(itemsJson || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function IngredientGroupNodeView({ node, updateAttributes, deleteNode }) {
  const { groupUuid, name, itemsJson } = node.attrs;
  const items = parseItems(itemsJson);
  const [editOpen, setEditOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);

  return (
    <NodeViewWrapper as="div" data-igroup={groupUuid} className="post-igroup-node">
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "grey.50",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.75,
            borderBottom: expanded ? "1px solid" : "none",
            borderColor: "divider",
          }}
        >
          <Box
            onClick={() => setExpanded((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              cursor: "pointer",
              minWidth: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }} noWrap>
              Ingredienser til {name || "gruppe"}
            </Typography>
            <IconButton size="small" tabIndex={-1}>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transform: expanded ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            </IconButton>
          </Box>
          <Typography
            component="button"
            type="button"
            variant="caption"
            onClick={() => setEditOpen(true)}
            sx={{
              border: 0,
              bgcolor: "transparent",
              color: "primary.main",
              cursor: "pointer",
              fontWeight: 600,
              px: 1,
              whiteSpace: "nowrap",
            }}
          >
            Rediger
          </Typography>
        </Box>
        <Collapse in={expanded}>
          <Box
            component="ul"
            onClick={() => setEditOpen(true)}
            sx={{ m: 0, px: 2.25, py: 1.1, pl: 3, cursor: "pointer" }}
          >
            {items.map((item, index) => {
              const note = (item.note || "").trim();
              return (
                <Typography
                  component="li"
                  key={`${item.ingredient_name}-${index}`}
                  variant="body2"
                  sx={{ mb: 0.35 }}
                >
                  <Box component="strong" sx={{ fontWeight: 700 }}>
                    {item.ingredient_name}
                  </Box>
                  {`, ${formatAmount(item.amount)} ${item.unit}`}
                  {note ? ` — ${note}` : null}
                </Typography>
              );
            })}
          </Box>
        </Collapse>
      </Box>
      <EditIngredientGroupModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        mode="edit"
        initial={{
          groupUuid,
          name,
          items,
        }}
        onSaved={(group) => {
          updateAttributes({
            groupUuid: group.groupUuid,
            name: group.name,
            itemsJson: JSON.stringify(group.items || []),
          });
        }}
        onDeleted={() => deleteNode()}
      />
    </NodeViewWrapper>
  );
}

export const IngredientGroup = Node.create({
  name: "ingredientGroup",
  group: "block",
  atom: true,
  draggable: false,
  selectable: true,

  addAttributes() {
    return {
      groupUuid: { default: null },
      name: { default: "Ingredients" },
      itemsJson: { default: "[]" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-type='ingredient-group']",
        getAttrs: (el) => ({
          groupUuid: el.getAttribute("data-group-uuid"),
          name: el.getAttribute("data-name") || "Ingredients",
          itemsJson: el.getAttribute("data-items") || "[]",
        }),
      },
    ];
  },

  renderHTML({ node }) {
    const id = node.attrs.groupUuid;
    return [
      "div",
      mergeAttributes({
        "data-type": "ingredient-group",
        "data-group-uuid": id,
        "data-name": node.attrs.name || "Ingredients",
        "data-items": node.attrs.itemsJson || "[]",
      }),
      igroupToken(id),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(IngredientGroupNodeView);
  },

  addCommands() {
    return {
      insertIngredientGroup:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              groupUuid: attrs.groupUuid,
              name: attrs.name || "Ingredients",
              itemsJson: JSON.stringify(attrs.items || []),
            },
          }),
    };
  },
});

export default IngredientGroup;
